import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from 'react';
import { adminApi, AdminProfile } from '../services/admin';

let adminAccessToken: string | null = null;
let userAccessToken: string | null = null;

export function getAdminAccessToken() {
  return adminAccessToken;
}
function setAdminAccessToken(token: string | null) {
  adminAccessToken = token;
}

import { api, setAccessToken as setUserAccessToken } from '../services/api';

api.interceptors.request.use((config) => {
  // Admin routes use admin token; user routes use user token (default behaviour from services/api.ts handles user).
  const url = config.url ?? '';
  if (url.startsWith('/admin/') || url.startsWith('admin/')) {
    if (adminAccessToken) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)['Authorization'] = `Bearer ${adminAccessToken}`;
    }
  }
  return config;
});

interface AdminAuthContextValue {
  admin: AdminProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((nextAdmin: AdminProfile, token: string) => {
    setAdminAccessToken(token);
    setAdmin(nextAdmin);
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await adminApi.login(username, password);
      apply(res.data.admin, res.data.accessToken);
    },
    [apply],
  );

  const logout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch {
      // ignore
    }
    setAdminAccessToken(null);
    setAdmin(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.refresh();
        if (!cancelled) {
          apply(res.data.admin, res.data.accessToken);
        }
      } catch {
        if (!cancelled) {
          setAdminAccessToken(null);
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      admin,
      isAuthenticated: !!admin,
      loading,
      login,
      logout,
    }),
    [admin, loading, login, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  return ctx;
}

// Helper: kept here so AdminAuthContext file owns the cross-token integration
export { setUserAccessToken, userAccessToken };
